import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { SiteButton } from "@/components/brand/SiteButton";
import { Container, Section, SectionHeading } from "@/components/layout/Section";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { SITE } from "@/config/site";
import { ROUTE_SEO } from "@/config/seoData";

const About = () => {
  const [page, setPage] = useState(null);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/pages/about").then((r) => setPage(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  const s = page?.sections || {};

  return (
    <>
      <Seo
        title={page?.seo?.title?.replace(" | A2Z Rankings", "") || ROUTE_SEO.about.title}
        description={page?.seo?.description || ROUTE_SEO.about.description}
        canonicalPath={ROUTE_SEO.about.canonicalPath}
        ogImage={ROUTE_SEO.about.ogImage}
        jsonLd={ROUTE_SEO.about.jsonLd}
      />
      <Section testid="about-page" className="pt-32 sm:pt-40">
        <Container className="space-y-16">
          <div className="max-w-3xl space-y-5">
            <p className="eyebrow">{s.eyebrow || "About us"}</p>
            <h1 className="font-display text-3xl font-bold tracking-tight text-brand-snow sm:text-4xl lg:text-5xl">
              {s.heading || "Engineers first. Marketers when it matters."}
            </h1>
          </div>
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="space-y-5 lg:col-span-7" data-testid="about-paragraphs">
              {(s.paragraphs || []).map((p, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="text-base leading-relaxed text-brand-mist"
                >
                  {p}
                </motion.p>
              ))}
            </div>
            <div className="lg:col-span-5">
              <div className="surface-card space-y-5 p-8" data-testid="about-values">
                <p className="eyebrow">How we operate</p>
                <ul className="space-y-4">
                  {(s.values || []).map((v, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-brand-snow">
                      <span className="mt-1 font-display text-xs font-bold text-brand-emerald">{String(i + 1).padStart(2, "0")}</span>
                      {v}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-6 space-y-3 rounded-xl border border-brand-mist/12 bg-brand-charcoal p-6 text-sm text-brand-mist" data-testid="about-contact-card">
                <p className="font-display text-sm font-semibold text-brand-snow">Registered & operating from India, working Australian hours.</p>
                <p>{SITE.indiaOffice}</p>
                <p>{SITE.email} · {SITE.phone}</p>
              </div>
            </div>
          </div>

          {faqs.length > 0 && (
            <div className="grid gap-10 border-t border-brand-mist/10 pt-16 lg:grid-cols-12 lg:gap-16" data-testid="about-faqs">
              <div className="lg:col-span-5">
                <SectionHeading
                  eyebrow="Straight answers"
                  title="Frequently asked questions"
                  description="The things every prospective client asks before the first call."
                  testid="faqs-heading"
                />
              </div>
              <div className="lg:col-span-7">
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-brand-mist/12">
                      <AccordionTrigger data-testid={`about-faq-trigger-${i}`} className="text-left font-display text-base font-semibold text-brand-snow hover:text-brand-emerald hover:no-underline">
                        {f.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm leading-relaxed text-brand-mist">{f.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-brand-emerald/20 bg-brand-charcoal p-10 text-center sm:p-14" data-testid="about-cta">
            <h2 className="mx-auto max-w-xl font-display text-2xl font-bold text-brand-snow sm:text-3xl">
              Sound like the team you'd want in your corner?
            </h2>
            <div className="mt-8 flex justify-center">
              <SiteButton to="/contact?source=about" testid="about-cta-contact" size="lg">Book a free discovery call</SiteButton>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
};

export default About;
